import React, {useEffect, useState} from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPills, faClinicMedical} from '@fortawesome/free-solid-svg-icons';
import {useBaseUrl} from '../utils/utils';
import localForage from 'localforage';
import Swal from 'sweetalert2';

interface Prescription {
    id: number;
    medication: string;
    dosage: string;
    clinic: string;
    created: string;
}

const PatientMedication: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "My Medications - Uzima Healthcare";
        (async () => {
            try {
                const patientId = await localForage.getItem<string>('patient_id');
                if (!patientId) return;
                const token = await localForage.getItem<string>('access_token');
                const response = await fetch(`${baseUrl}facilities/patient-records/${patientId}`, {
                    headers: {Authorization: token ? `Bearer ${token}` : ''},
                });
                const data = await response.json();
                if (data.status === 'success') {
                    setPrescriptions(data.data || []);
                }
            } catch (error) {
                console.error('Error fetching medications:', error);
                Swal.fire('Error', 'Failed to load your medications', 'error');
            } finally {
                setLoading(false);
            }
        })();
    }, [baseUrl]);

    return (
        <PatientDashboardLayout>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <h2 className="mb-1">
                            <FontAwesomeIcon icon={faPills} className="me-2 text-primary"/>
                            My Medications
                        </h2>
                        <p className="text-muted">Prescriptions issued to you across all clinics</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : prescriptions.length === 0 ? (
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <FontAwesomeIcon icon={faPills} size="3x" className="text-muted mb-3"/>
                            <h5>No Medications on Record</h5>
                            <p className="text-muted">Prescriptions issued by your clinics will appear here.</p>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        {prescriptions.map((rx) => (
                            <div key={rx.id} className="col-md-6 col-lg-4 mb-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h5 className="card-title mb-1">{rx.medication}</h5>
                                        <p className="text-muted mb-2">{rx.dosage}</p>
                                        <div className="small">
                                            <FontAwesomeIcon icon={faClinicMedical} className="me-1 text-muted"/>
                                            {rx.clinic}
                                        </div>
                                        <div className="small text-muted mt-1">
                                            {new Date(rx.created).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientMedication;
